'use client';
import { useState } from 'react';
import { CAP_MAP, CAP_COLORS, getCapLabel } from '@/lib/capabilityMap';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';
import type { CapabilityItem } from '@/types/pdp';

const SHOW_LIMIT = 8;

interface Props {
  capabilities: CapabilityItem[];
  lang?: string;
}

export function CapabilityList({ capabilities }: Props) {
  const { lang } = usePriceConvert();
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  const mapped = capabilities
    .map(c => {
      const info = CAP_MAP[c.key];
      if (!info) return null;
      const label = getCapLabel(info, c.min_players ?? null, c.max_players ?? null, lang);
      const colors = CAP_COLORS[info.category];
      return { key: c.key, label, colors, priority: info.priority };
    })
    .filter(Boolean)
    .sort((a, b) => a!.priority - b!.priority) as NonNullable<ReturnType<typeof capabilities['map']>[number]>[];

  if (mapped.length === 0) return null;

  const visible = expanded ? mapped : mapped.slice(0, SHOW_LIMIT);
  const hasMore = mapped.length > SHOW_LIMIT;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {visible.map((c: any) => (
          <span key={c.key} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 600, padding: '5px 12px',
            borderRadius: 999,
            background: 'var(--v4-panel)',
            border: '1px solid var(--v4-border)',
            color: 'var(--v4-fg-2)',
            whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.colors.text, flexShrink: 0 }} />
            {c.label}
          </span>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            marginTop: 10, fontSize: 11, fontWeight: 600,
            color: 'var(--v4-fg-3)', background: 'none',
            border: 'none', padding: 0, cursor: 'pointer',
            letterSpacing: '.02em',
          }}
        >
          {expanded ? `${t('pdp_collapse')} ↑` : `${t('pdp_expand')} ${mapped.length} →`}
        </button>
      )}
    </div>
  );
}
